import cx from 'classnames';
import React from 'react';
import type { ReactNode } from 'react';

import moment from 'moment';

import type { ZonedDateTime } from '@internationalized/date';

import type { TimeValue } from '@react-types/datepicker';

import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';

import { TimeField } from '../field-date-picker-react-aria/controls/time-field';
import { toTZDate } from '../field-date-picker-react-aria/utils';

export interface IFieldTimePickerProps extends Omit<IFieldProps, 'label' | 'minValue'> {
  className: string;
  containerClass?: string;
  defaultValue: string;
  displayFormat?: string;
  errorText?: string;
  id?: string;
  label?: ReactNode | string;
  labelClass?: string;
  minValue?: string | Date;
  maxValue?: string | Date;
  placeholder?: string;
  required?: boolean;
  submitted?: boolean;
  isReadOnly?: boolean;
  value?: string | Date;
  valueFormat?: string;

  onChange?: (time: string) => void;
}

const useDate = (
  value: string | Date | undefined
): [moment.Moment | undefined, ZonedDateTime | undefined] => {
  return React.useMemo(() => {
    if (!value) {
      return [undefined, undefined];
    }

    const date = moment(value);
    return [date, toTZDate(date)];
  }, [value]);
};

const useValue = (value: string | Date | undefined): [moment.Moment, ZonedDateTime | undefined] => {
  return React.useMemo(() => {
    const date = value ? moment(value) : moment();

    return [date, toTZDate(date)];
  }, [value]);
};

const FieldTimePickerAria: React.FC<IFieldTimePickerProps> = (props) => {
  const {
    disabled,
    minValue,
    maxValue,
    containerClass,
    labelClass,
    className,
    label,
    required,
    errorText,
    submitted,
    isReadOnly,
    onChange: propsOnChange
  } = props;

  const [propDate, propValue] = useValue(props.value);

  const [minDate, min] = useDate(minValue);
  const [maxDate, max] = useDate(maxValue);

  const error = React.useMemo<String | undefined>(() => {
    return errorText && submitted ? errorText.split(',').at(-1) : undefined;
  }, [submitted, errorText]);

  const onChange = React.useCallback(
    (event: TimeValue) => {
      const newDate = moment(propDate).hour(event.hour).minute(event.minute);

      if ((minDate && newDate.isBefore(minDate)) || (maxDate && newDate.isAfter(maxDate))) {
        return;
      }

      propsOnChange?.(newDate.toISOString(false));
    },
    [propDate, minDate, maxDate, propsOnChange]
  );

  return (
    <div className={cx('field-container w-full', containerClass)}>
      <label className={cx('click-label', labelClass)}>
        <span>
          {label}
          {required && <span className="text-normal pl-px text-rose-600">*</span>}
        </span>
      </label>
      <div
        className={cx('click-input relative flex items-center justify-between', className, {
          'bg-white': !disabled,
          'bg-gray-100': disabled
        })}
      >
        <div className="ml-2">
          <TimeField
            isDisabled={disabled}
            isReadOnly={isReadOnly}
            granularity={'minute'}
            hideTimeZone={true}
            hourCycle={24}
            maxValue={max}
            minValue={min}
            value={propValue}
            onChange={onChange}
          />
        </div>
      </div>
      {!!error && (
        <div className="mt-1 pb-2 text-xs font-medium text-rose-500">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default class FieldTimePicker extends React.Component<IFieldTimePickerProps> {
  static defaultProps = {
    defaultValue: '',
    displayFormat: 'HH:mm A',
    valueFormat: 'HH:mm'
  };

  render() {
    return <FieldTimePickerAria {...this.props} />;
  }
}
